import React, { useEffect, useRef, useState } from "react";
import {
    FaFileAlt,
    FaFileExcel,
    FaFilePdf,
    FaFileWord,
    FaMicrophone,
    FaRegStopCircle,
    FaVolumeUp,
} from "react-icons/fa";
import { FiCheck, FiCopy, FiDownload, FiExternalLink, FiUpload } from "react-icons/fi";
import { IoIosSend } from "react-icons/io";
import { useHistory, useLocation } from "react-router-dom";
import { createErrorAlert, updateUser } from "../../redux/actions";
import { updatedUserLoader } from "../../redux/selectors";
import { UpdateUser } from "../../services";
import { apiRequest, setUser } from "../../helpers";
import { useDispatch } from "react-redux";
// import { toast } from "react-toastify";

const API_BASE = "https://aibackend.studycrowdai.us/v1";
const AUTH_TOKEN = "Bearer app-mwVnSr9CJyRFLgcfBn49dXMM";
const SUPPORTED_DOCUMENT_EXTENSIONS = [
    "txt",
    "md",
    "mdx",
    "markdown",
    "pdf",
    "html",
    "xlsx",
    "xls",
    "doc",
    "docx",
    "csv",
    "eml",
    "msg",
    "pptx",
    "ppt",
    "xml",
    "epub",
];
const SUPPORTED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
const SUPPORTED_FILE_EXTENSIONS = [
    ...SUPPORTED_DOCUMENT_EXTENSIONS,
    ...SUPPORTED_IMAGE_EXTENSIONS,
];
const SUPPORTED_FILE_ACCEPT = SUPPORTED_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(",");
const SUPPORTED_DOCUMENT_LABEL = "TXT, MD, MDX, MARKDOWN, PDF, HTML, XLSX, XLS, DOC, DOCX, CSV, EML, MSG, PPTX, PPT, XML, EPUB";
const SUPPORTED_IMAGE_LABEL = "JPG, JPEG, PNG, GIF, WEBP, SVG";

const decodeHtmlEntities = (text = "") => {
    if (typeof document === "undefined") return text;

    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
};

const decodeEscapedSequences = (text = "") => {
    return text
        .replace(/\\u\{([0-9a-fA-F]+)\}/g, (_, hex) => {
            try {
                return String.fromCodePoint(parseInt(hex, 16));
            } catch (error) {
                return _;
            }
        })
        .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(/\\x([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t")
        .replace(/\\b/g, "")
        .replace(/\\f/g, "")
        .replace(/\\"/g, "\"")
        .replace(/\\'/g, "'")
        .replace(/\\\\/g, "\\");
};

const normalizeMathText = (text = "") => {
    const latexCommands = {
        alpha: "alpha",
        beta: "beta",
        gamma: "gamma",
        delta: "delta",
        epsilon: "epsilon",
        theta: "theta",
        lambda: "lambda",
        mu: "mu",
        pi: "pi",
        sigma: "sigma",
        omega: "omega",
        log: "log",
        ln: "ln",
        sum: "sum",
        sqrt: "sqrt",
        times: "x",
        cdot: "·",
        leq: "<=",
        geq: ">=",
        neq: "!=",
        approx: "~",
        rightarrow: "->",
        leftarrow: "<-",
        to: "->",
        infty: "infinity",
    };

    return text
        .replace(/\$\$([\s\S]*?)\$\$/g, "$1")
        .replace(/\$([^$]+)\$/g, "$1")
        .replace(/\\\[(.*?)\\\]/gs, "$1")
        .replace(/\\\((.*?)\\\)/gs, "$1")
        .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
        .replace(/\\sqrt\{([^{}]+)\}/g, "sqrt($1)")
        .replace(/\{([^{}]+)\}/g, "$1")
        .replace(/\\([a-zA-Z]+)/g, (match, command) => latexCommands[command] || command)
        .replace(/\\([_{}#$%&])/g, "$1");
};

const normalizeChatText = (text = "") => {
    let normalized = String(text || "")
        .replace(/<think>[\s\S]*?<\/think>/g, "")
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .trim();

    if (/&(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);/.test(normalized)) {
        normalized = decodeHtmlEntities(normalized);
    }

    if (/\\(?:u\{|u[0-9a-fA-F]{4}|x[0-9a-fA-F]{2}|[nrtbf"'\\])/.test(normalized)) {
        normalized = decodeEscapedSequences(normalized);
    }

    return normalizeMathText(normalized)
        .replace(/[ \t]+\n/g, "\n")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/\n{3,}/g, "\n\n");
};

const renderInlineMarkdown = (text = "") => {
    const parts = String(text).split(/(`[^`]+`|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|\*[^*\n]+\*|_[^_\n]+_)/g);

    return parts.map((part, index) => {
        if (part.startsWith("`") && part.endsWith("`")) {
            return (
                <code
                    key={index}
                    style={{
                        padding: "0.08rem 0.25rem",
                        borderRadius: "0.25rem",
                        backgroundColor: "rgba(0, 0, 0, 0.08)",
                        fontFamily: "monospace",
                        fontSize: "0.92em",
                    }}
                >
                    {part.slice(1, -1)}
                </code>
            );
        }

        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) {
            const href = link[2];
            const isSafeUrl = /^(https?:|mailto:|tel:)/i.test(href);

            return isSafeUrl ? (
                <a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#CC6828", textDecoration: "underline" }}
                >
                    {link[1]}
                </a>
            ) : (
                link[1]
            );
        }

        if ((part.startsWith("**") && part.endsWith("**")) || (part.startsWith("__") && part.endsWith("__"))) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }

        if (part.startsWith("~~") && part.endsWith("~~")) {
            return <del key={index}>{part.slice(2, -2)}</del>;
        }

        if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_"))) {
            return <em key={index}>{part.slice(1, -1)}</em>;
        }

        return part;
    });
};

const renderChatText = (text = "") => {
    const normalized = normalizeChatText(text);
    const lines = normalized.split("\n");
    const elements = [];
    let listItems = [];
    let orderedItems = [];
    let tableRows = [];
    let codeLines = [];
    let isCodeBlock = false;

    const flushList = () => {
        if (!listItems.length) return;

        elements.push(
            <ul key={`list-${elements.length}`} style={{ margin: "0.25rem 0", paddingLeft: "1.25rem" }}>
                {listItems.map((item, index) => (
                    <li key={index} style={{ margin: "0.2rem 0" }}>
                        {renderInlineMarkdown(item)}
                    </li>
                ))}
            </ul>
        );
        listItems = [];
    };

    const flushOrderedList = () => {
        if (!orderedItems.length) return;

        elements.push(
            <ol key={`ordered-${elements.length}`} style={{ margin: "0.25rem 0", paddingLeft: "1.25rem" }}>
                {orderedItems.map((item, index) => (
                    <li key={index} style={{ margin: "0.25rem 0" }}>
                        {renderInlineMarkdown(item)}
                    </li>
                ))}
            </ol>
        );
        orderedItems = [];
    };

    const flushLists = () => {
        flushList();
        flushOrderedList();
    };

    const flushTable = () => {
        if (!tableRows.length) return;

        const rows = tableRows
            .map((row) => row.split("|").map((cell) => cell.trim()).filter(Boolean))
            .filter((row) => !row.every((cell) => /^:?-{3,}:?$/.test(cell)));

        if (rows.length) {
            elements.push(
                <div key={`table-${elements.length}`} style={{ overflowX: "auto", marginTop: "0.45rem" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9rem" }}>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => {
                                        const Cell = rowIndex === 0 ? "th" : "td";
                                        return (
                                            <Cell
                                                key={cellIndex}
                                                style={{
                                                    border: "1px solid rgba(0, 0, 0, 0.15)",
                                                    padding: "0.35rem 0.45rem",
                                                    textAlign: "left",
                                                }}
                                            >
                                                {renderInlineMarkdown(cell)}
                                            </Cell>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        tableRows = [];
    };

    const flushCodeBlock = () => {
        if (!codeLines.length) return;

        elements.push(
            <pre
                key={`code-${elements.length}`}
                style={{
                    margin: "0.5rem 0 0",
                    padding: "0.75rem",
                    overflowX: "auto",
                    borderRadius: "0.4rem",
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                    whiteSpace: "pre-wrap",
                }}
            >
                <code style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
                    {codeLines.join("\n")}
                </code>
            </pre>
        );
        codeLines = [];
    };

    const flushAll = () => {
        flushLists();
        flushTable();
        flushCodeBlock();
    };

    lines.forEach((line, index) => {
        const trimmed = line.trim();

        if (trimmed.startsWith("```")) {
            if (isCodeBlock) {
                isCodeBlock = false;
                flushCodeBlock();
            } else {
                flushAll();
                isCodeBlock = true;
            }
            return;
        }

        if (isCodeBlock) {
            codeLines.push(line);
            return;
        }

        if (!trimmed) {
            flushAll();
            return;
        }

        const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
        if (heading) {
            flushAll();
            elements.push(
                <div
                    key={`heading-${index}`}
                    style={{
                        fontWeight: 700,
                        fontSize: heading[1].length <= 2 ? "1rem" : "0.95rem",
                        margin: elements.length ? "0.65rem 0 0.25rem" : "0 0 0.25rem",
                    }}
                >
                    {renderInlineMarkdown(heading[2])}
                </div>
            );
            return;
        }

        if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
            flushAll();
            elements.push(<hr key={`hr-${index}`} style={{ border: 0, borderTop: "1px solid rgba(0, 0, 0, 0.15)", margin: "0.65rem 0" }} />);
            return;
        }

        const quote = trimmed.match(/^>\s+(.+)$/);
        if (quote) {
            flushAll();
            elements.push(
                <blockquote
                    key={`quote-${index}`}
                    style={{
                        margin: "0.5rem 0 0",
                        paddingLeft: "0.75rem",
                        borderLeft: "3px solid rgba(0, 0, 0, 0.25)",
                        color: "#374151",
                    }}
                >
                    {renderInlineMarkdown(quote[1])}
                </blockquote>
            );
            return;
        }

        if (/^\|.+\|$/.test(trimmed)) {
            flushLists();
            tableRows.push(trimmed);
            return;
        }

        const bullet = trimmed.match(/^[-*]\s+(.+)$/);
        if (bullet) {
            flushOrderedList();
            flushTable();
            listItems.push(bullet[1]);
            return;
        }

        const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
        if (ordered) {
            flushList();
            flushTable();
            orderedItems.push(ordered[1]);
            return;
        }

        flushAll();
        elements.push(
            <p key={`p-${index}`} style={{ margin: elements.length ? "0.45rem 0 0" : 0 }}>
                {renderInlineMarkdown(trimmed)}
            </p>
        );
    });

    flushAll();

    return elements.length ? elements : normalized;
};

const getFileExtension = (fileName = "", url = "") => {
    const safeUrl = typeof url === "string" ? url : "";
    const safeFileName = typeof fileName === "string" ? fileName : "";
    const value = safeFileName || safeUrl.split("?")[0] || "";
    const extension = value.split(".").pop();

    return extension && extension !== value ? extension.toLowerCase() : "file";
};

const getFileIcon = (extension) => {
    if (extension === "pdf") return <FaFilePdf />;
    if (["doc", "docx"].includes(extension)) return <FaFileWord />;
    if (["xls", "xlsx", "xlxs", "csv"].includes(extension)) return <FaFileExcel />;

    return <FaFileAlt />;
};

const isSupportedFile = (selectedFile) => {
    if (!selectedFile) return false;

    return SUPPORTED_FILE_EXTENSIONS.includes(getFileExtension(selectedFile.name));
};

const TOKENS_PER_MILLION = 1000000;
const CREDITS_PER_MILLION_TOKENS = 19.11;

export function ChatInterface({ credits, onSessionCreated, updateUser,
    updatedUserLoader }) {
    const navigate = useHistory();
    const location = useLocation();
    const user = JSON.parse(sessionStorage?.getItem("app_user"));
    const totalTokens = user?.total_tokens || 0;
    const displayCredits = Math.round(
        (totalTokens / TOKENS_PER_MILLION) * CREDITS_PER_MILLION_TOKENS
    );
    const hasLowCredits = displayCredits < 10;
    const stopRef = useRef(false);
    const messageContainer = useRef();
    const dispatch = useDispatch()

    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [file, setFile] = useState(null);
    const [uploadedFileMeta, setUploadedFileMeta] = useState(null);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [speakingId, setSpeakingId] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [hoveredCopyId, setHoveredCopyId] = useState(null);
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const copyTimeoutRef = useRef(null);
    const dragDepthRef = useRef(0);
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null);
    const [conversationId, setConversationId] = useState("");
    const [messages, setMessages] = useState([
        {
            id: "welcome",
            from: "ai",
            text: `Hi ${user?.first_name || user?.user_name || "there"}, how can I help you today?`,
            timestamp: new Date(),
        },
    ]);

    const params = new URLSearchParams(location.search);
    const userId = params.get("user_id") || params.get("userid");
    const sessionFromUrl = params.get("session_id");

    const scrollToBottom = () => {
        if (messageContainer.current) {
            messageContainer.current.scrollTop = messageContainer.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }

            if (audioRef.current) {
                audioRef.current.pause();
            }

            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!userId) return;

        const searchParams = new URLSearchParams(location.search);
        const isNew = searchParams.get("new") === "1";
        const sessionFromUrl = searchParams.get("session_id");

        if (sessionFromUrl && !isNew) {
            setConversationId(sessionFromUrl);
            fetchMessages(sessionFromUrl);
            return;
        }

        setConversationId("");
        setMessages([
            {
                id: "welcome",
                from: "ai",
                text: `Hi ${user?.first_name || user?.user_name || "there"}, how can I help you today?`,
                timestamp: new Date(),
            },
        ]);
        setFile(null);
        setPreviewUrl(null);
        setUploadedFileMeta(null);
        setInput("");
        setTaskId(null);
        setIsLoading(false);
    }, [location.search, userId]);

    const fetchMessages = async (sessionId) => {
        try {
            const res = await fetch(
                `${API_BASE}/messages?user=${userId}&conversation_id=${sessionId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: AUTH_TOKEN,
                    },
                }
            );

            const response = await res.json();
            const data = response?.data || [];

            const formatted = [
                {
                    id: "welcome",
                    from: "ai",
                    text: `Hi ${user?.first_name || user?.user_name || "there"}, how can I help you today?`,
                    timestamp: new Date(),
                },
            ];

            data.forEach((msg) => {
                if (msg.answer === "Hello! How can I help you today?") return;

                const firstFile = msg.message_files?.[0] || null;

                if (msg.query) {
                    formatted.push({
                        id: `${msg.id}-user`,
                        from: "user",
                        text: msg.query,
                        file_url: firstFile?.url || null,
                        fileType: firstFile?.mime_type || null,
                        fileName: firstFile?.filename || null,
                        uploadFileId: firstFile?.upload_file_id || null,
                        timestamp: new Date(msg.created_at * 1000),
                    });
                }

                if (msg.status === "error") {
                    let errorText = msg.error || "Something went wrong. Please try again.";

                    if (errorText.includes("Run failed:")) {
                        errorText = "Unable to generate response";
                    }

                    formatted.push({
                        id: `${msg.id}-error`,
                        from: "ai",
                        text: errorText,
                        timestamp: new Date(msg.created_at * 1000),
                    });
                } else if (msg.answer) {
                    formatted.push({
                        id: `${msg.id}-ai`,
                        messageId: msg.id,
                        from: "ai",
                        text: normalizeChatText(msg.answer),
                        timestamp: new Date(msg.created_at * 1000),
                    });
                }
            });

            setMessages(formatted);
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const typeText = async (fullText, delay = 20) => {
        return new Promise((resolve) => {
            if (!fullText) {
                resolve();
                return;
            }

            let currentText = "";
            let i = 0;

            const interval = setInterval(() => {
                if (stopRef.current) {
                    clearInterval(interval);
                    resolve();
                    return;
                }

                currentText += fullText[i];

                setMessages((prev) => {
                    const updated = [...prev];
                    if (updated.length > 0) {
                        updated[updated.length - 1] = {
                            ...updated[updated.length - 1],
                            text: currentText,
                        };
                    }
                    return updated;
                });

                i++;
                scrollToBottom();

                if (i >= fullText.length) {
                    clearInterval(interval);
                    resolve();
                }
            }, delay);
        });
    };

    const handleMicClick = () => {
        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();

        recognition.lang = "en-US";

        recognition.onresult = (event) => {
            const text = event.results[0][0].transcript;
            setInput(text);
        };

        recognition.start();
    };

    // const handleMicClick = async () => {
    //     if (!isRecording) {
    //         const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    //         const mediaRecorder = new MediaRecorder(stream);
    //         mediaRecorderRef.current = mediaRecorder;
    //         audioChunksRef.current = [];

    //         mediaRecorder.ondataavailable = (event) => {
    //             audioChunksRef.current.push(event.data);
    //         };

    //         mediaRecorder.onstop = handleAudioUpload;

    //         mediaRecorder.start();
    //         setIsRecording(true);
    //     } else {
    //         mediaRecorderRef.current.stop();
    //         setIsRecording(false);
    //     }
    // };

    const handleAudioUpload = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });

        const formData = new FormData();
        formData.append("file", audioBlob, "voice.webm");

        try {
            const res = await fetch(`${API_BASE}/audio-to-text`, {
                method: "POST",
                headers: {
                    Authorization: AUTH_TOKEN,
                },
                body: formData,
            });

            const data = await res.json();

            setInput(data?.text || "");

        } catch (err) {
            console.error("Audio upload error:", err);
        }
    };

    const handleCopy = async (text, messageId = null) => {
        try {
            await navigator.clipboard.writeText(text);

            if (messageId) {
                setCopiedMessageId(messageId);

                if (copyTimeoutRef.current) {
                    clearTimeout(copyTimeoutRef.current);
                }

                copyTimeoutRef.current = setTimeout(() => {
                    setCopiedMessageId(null);
                }, 1800);
            }
        } catch (error) {
            console.error("Unable to copy message:", error);
        }
    };

    const handleDownloadFile = async (url, fileName = "image") => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Unable to download file:", error);
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const handleOpenFile = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };


    const handleSpeak = async (message) => {
        if (!message?.text) return;

        handleStopSpeak();
        setSpeakingId(message.id);

        try {
            const response = await fetch(`${API_BASE}/text-to-audio`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
                body: JSON.stringify({
                    message_id: "",
                    text: message.text,
                    user: userId || user?.id || "anonymous",
                }),
            });

            if (!response.ok) {
                throw new Error("Unable to generate audio response");
            }

            const audioBlob = await response.blob();
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);

            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
            }

            audioRef.current = audio;
            audioUrlRef.current = audioUrl;
            audio.onended = () => {
                setSpeakingId(null);
                URL.revokeObjectURL(audioUrl);
                audioUrlRef.current = null;
                audioRef.current = null;
            };
            audio.onerror = () => {
                setSpeakingId(null);
                URL.revokeObjectURL(audioUrl);
                audioUrlRef.current = null;
                audioRef.current = null;
            };

            await audio.play();
        } catch (error) {
            console.error("Unable to play audio response:", error);
            setSpeakingId(null);
        }
    };

    const handleStopSpeak = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }

        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }

        setSpeakingId(null);
    };

    const handleSend = async () => {
        stopRef.current = false;

        if (input.trim() === "" && !uploadedFileMeta) return;

        const localFileData = uploadedFileMeta
            ? {
                file_url: uploadedFileMeta.source_url || previewUrl,
                fileType: uploadedFileMeta.mime_type || file?.type || null,
                fileName: uploadedFileMeta.name || file?.name || null,
                uploadFileId: uploadedFileMeta.id,
            }
            : null;

        const userMessage = {
            id: `user-${Date.now()}`,
            from: "user",
            text: input,
            ...(localFileData || {}),
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        scrollToBottom();

        const currentInput = input;
        const currentUploadedFileMeta = uploadedFileMeta;

        setInput("");
        setPreviewUrl(null);
        setFile(null);
        setUploadedFileMeta(null);
        setIsLoading(true);

        const payload = {
            inputs: {},
            user: userId,
            conversation_id: conversationId || "",
            query: currentInput,
            response_mode: "blocking",
            files: currentUploadedFileMeta
                ? [
                    {
                        type: currentUploadedFileMeta.mime_type?.startsWith("image/")
                            ? "image"
                            : "document",
                        transfer_method: "local_file",
                        url: "",
                        upload_file_id: currentUploadedFileMeta.id,
                    },
                ]
                : [],
        };

        try {
            let response
            if (user?.total_tokens === 0) {
                dispatch(createErrorAlert(400, '', 'Your tokens have expired. Please recharge.', 400))
                // toast.error("Your tokens have expired. Please recharge.");
                return;
            }
            // if(user?.total_tokens !== 0){
            response = await fetch(`${API_BASE}/chat-messages`, {
                method: "POST",
                body: JSON.stringify(payload),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
            });
            // }

            const res = await response.json();
            const newConversationId = res?.conversation_id;

            if (res?.task_id) setTaskId(res.task_id);

            if (newConversationId && newConversationId !== conversationId) {
                setConversationId(newConversationId);
                const url = new URL(window.location);
                url.searchParams.set("session_id", newConversationId);
                url.searchParams.delete("new");
                window.history.replaceState({}, "", url);
                onSessionCreated?.();
            }

            if (res?.answer) {
                const aiText = normalizeChatText(res.answer);

                setMessages((prev) => [
                    ...prev,
                    {
                        id: `ai-${Date.now()}`,
                        messageId: res?.message_id,
                        from: "ai",
                        text: "",
                    },
                ]);

                await typeText(aiText);
            } else if (res?.status === "error" || res?.error) {
                let errorText = user?.total_tokens === 0 ? 'Token expired' : res?.error || "Something went wrong. Please try again.";

                if (errorText.includes("Run failed:")) {
                    errorText = "Unable to generate response";
                }

                setMessages((prev) => [
                    ...prev,
                    {
                        id: `ai-error-${Date.now()}`,
                        from: "ai",
                        text: errorText,
                    },
                ]);
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `ai-empty-${Date.now()}`,
                        from: "ai",
                        text: "No response from AI.",
                    },
                ]);
            }
            console.log(res, "response from chat-message")
            const payloadUser = {
                username: user?.username,
                email: user?.email,
                input_tokens: res?.metadata?.usage?.prompt_tokens,
                output_tokens: res?.metadata?.usage?.completion_tokens,
                total_tokens: res?.metadata?.usage?.total_tokens,
            }
            const data = await apiRequest({
                method: 'put',
                url: 'accounts/use-tokens/',
                data: payloadUser
            }).then((resToken) => {
            }).catch((e) => {
            })
        } catch (error) {
            console.error("Error sending message:", error);
            setMessages((prev) => [
                ...prev,
                {
                    id: `ai-error-${Date.now()}`,
                    from: "ai",
                    text: "Sorry, something went wrong. Please try again.",
                },
            ]);
        } finally {
            setIsLoading(false);
            setTaskId(null);
        }
    };

    const handleStop = async () => {
        stopRef.current = true;

        if (!taskId) {
            setIsLoading(false);
            return;
        }

        try {
            await fetch(`${API_BASE}/chat-messages/${taskId}/stop`, {
                method: "POST",
                body: JSON.stringify({ user: userId }),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
            });
        } catch (e) {
            console.error("Stop error:", e);
        } finally {
            setIsLoading(false);
            setTaskId(null);
        }
    };

    const uploadSelectedFile = async (selectedFile) => {
        if (!selectedFile) return;

        if (!isSupportedFile(selectedFile)) {
            dispatch(createErrorAlert(
                400,
                "",
                `Unsupported file type. Supported documents: ${SUPPORTED_DOCUMENT_LABEL}. Supported images: ${SUPPORTED_IMAGE_LABEL}.`,
                400
            ));
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        try {
            const res = await fetch(`${API_BASE}/files/upload`, {
                method: "POST",
                headers: {
                    Authorization: AUTH_TOKEN,
                },
                body: formData,
            });

            const data = await res.json();

            setUploadedFileMeta(data);
            setFile(selectedFile);

            if (selectedFile.type.startsWith("image/")) {
                setPreviewUrl(URL.createObjectURL(selectedFile));
            } else {
                setPreviewUrl(null);
            }
        } catch (err) {
            console.error("Upload error:", err);
        }
    };

    const handleFileUpload = async (e) => {
        await uploadSelectedFile(e.target.files[0]);
        e.target.value = "";
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDraggingFile(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);

        if (dragDepthRef.current === 0) {
            setIsDraggingFile(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDraggingFile(false);
        await uploadSelectedFile(e.dataTransfer.files[0]);
    };

    const handleQuickAction = (action) => {
        const prompts = {
            "Assignment help": "I need help with my assignment. Can you guide me through it?",
            "Exam preparation": "Help me prepare for my upcoming exam. What should I focus on?",
            "Summarize my lecture": "Can you help me summarize my lecture notes?",
            "Research": "I need help with research for my topic. Where should I start?",
        };
        setInput(prompts[action] || action);
    };

    return (
        <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
                position: "relative",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                backgroundColor: "#f8f9fa",
            }}
        >
            {isDraggingFile && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.65rem",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.78)",
                        color: "#CC6828",
                        pointerEvents: "none",
                        zIndex: 30,
                        textAlign: "center",
                        padding: "1.5rem",
                    }}
                >
                    <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
                        Drop file to upload
                    </div>
                    <div style={{ maxWidth: "44rem", color: "#374151", fontSize: "0.9rem", lineHeight: 1.45 }}>
                        <strong>Documents:</strong> {SUPPORTED_DOCUMENT_LABEL}
                    </div>
                    <div style={{ maxWidth: "44rem", color: "#374151", fontSize: "0.9rem", lineHeight: 1.45 }}>
                        <strong>Images:</strong> {SUPPORTED_IMAGE_LABEL}
                    </div>
                </div>
            )}
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "100%",
                    margin: "0 auto",
                    width: "100%",
                }}
            >
                {messages.length === 1 && (
                    <div style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
                        <div
                            style={{
                                width: "4rem",
                                height: "4rem",
                                borderRadius: "9999px",
                                backgroundColor: "#CC6828",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 1.5rem",
                            }}
                        >
                            <span role="img" aria-label="bot" style={{ fontSize: "1.5rem", color: "white" }}>
                                🤖
                            </span>
                        </div>
                        <h1 style={{ fontSize: "1.875rem", fontWeight: "bold", marginBottom: "1rem" }}>
                            Hey, I'm your AI tutor, Clara!
                        </h1>
                        <p style={{ color: "#6c757d", maxWidth: "36rem", margin: "0 auto 0.5rem" }}>
                            I am trained with high-quality academic resources to provide accurate responses.
                        </p>
                        <p style={{ color: "#6c757d", maxWidth: "36rem", margin: "0 auto 1.5rem" }}>
                            I adapt to your course and offer personalized guidance to help you ace your exams!
                        </p>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "0.5rem",
                                marginBottom: "2rem",
                            }}
                        >
                            <span
                                style={{
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "9999px",
                                    padding: "0.25rem 0.75rem",
                                    fontSize: "0.875rem",
                                }}
                            >
                                C
                            </span>
                            <button
                                type="button"
                                onClick={() => navigate.push("/aitutor/creditsbot")}
                                style={{
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "9999px",
                                    border: "none",
                                    padding: "0.25rem 0.75rem",
                                    cursor: "pointer",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {displayCredits} credits
                            </button>
                        </div>
                        <div style={{ maxWidth: "36rem", margin: "0 auto" }}>
                            <p style={{ fontSize: "0.875rem", color: "#6c757d", marginBottom: "1rem" }}>
                                What can I help you with today?
                            </p>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                                    gap: "0.75rem",
                                }}
                            >
                                {["Assignment help", "Exam preparation", "Summarize my lecture", "Research"].map(
                                    (option) => (
                                        <button
                                            key={option}
                                            onClick={() => handleQuickAction(option)}
                                            style={{
                                                padding: "1rem",
                                                backgroundColor: "#fff",
                                                border: "1px solid #ccc",
                                                borderRadius: "0.5rem",
                                                cursor: "pointer",
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            {option}
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        height: messages.length === 1 ? "20vh" : "90vh",
                    }}
                >
                    <div
                        ref={messageContainer}
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            padding: "1rem 1.5rem",
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                        }}
                    >
                        {messages.map((message) => {
                            const isUser = message.from === "user";
                            const isCopied = copiedMessageId === message.id;
                            const attachmentUrl = message.file_url;
                            const isImageAttachment =
                                Boolean(attachmentUrl) &&
                                (message.fileType?.startsWith("image/") ||
                                    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(attachmentUrl));
                            const attachmentExtension = getFileExtension(message.fileName, attachmentUrl);

                            return (
                                <div
                                    key={message.id}
                                    onMouseEnter={() => setHoveredId(message.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    style={{
                                        display: "flex",
                                        justifyContent: isUser ? "flex-end" : "flex-start",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    {/* LEFT SIDE (for USER messages → icons first) */}
                                    {isUser && (
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "6px",
                                                opacity: hoveredId === message.id ? 1 : 0,
                                                transition: "0.2s",
                                            }}
                                        >
                                            {attachmentUrl ? (
                                                <>
                                                    {!isImageAttachment && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenFile(attachmentUrl)}
                                                            aria-label="Open file"
                                                            style={{
                                                                border: "none",
                                                                background: "transparent",
                                                                padding: 0,
                                                                color: "#111827",
                                                                display: "inline-flex",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            <FiExternalLink />
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadFile(attachmentUrl, message.fileName || `attachment.${attachmentExtension}`)}
                                                        aria-label="Download file"
                                                        style={{
                                                            border: "none",
                                                            background: "transparent",
                                                            padding: 0,
                                                            color: "#111827",
                                                            display: "inline-flex",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <FiDownload />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <FiCopy onClick={() => handleCopy(message.text)} style={{ cursor: "pointer" }} />

                                                    {speakingId === message.id ? (
                                                        <FaRegStopCircle
                                                            onClick={handleStopSpeak}
                                                            style={{ cursor: "pointer", color: "red" }}
                                                        />
                                                    ) : (
                                                        <FaVolumeUp
                                                            onClick={() => handleSpeak(message)}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: isUser ? "flex-end" : "flex-start",
                                            maxWidth: "24rem",
                                            gap: "0.4rem",
                                        }}
                                    >
                                        {isUser && attachmentUrl && (
                                            isImageAttachment ? (
                                                <img
                                                    src={attachmentUrl}
                                                    alt={message.fileName || "Uploaded file"}
                                                    style={{
                                                        maxWidth: "16rem",
                                                        maxHeight: "12rem",
                                                        borderRadius: "0.5rem",
                                                        objectFit: "cover",
                                                        border: "1px solid #e5e7eb",
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.7rem",
                                                        maxWidth: "18rem",
                                                        padding: "0.7rem 0.85rem",
                                                        borderRadius: "0.5rem",
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #e5e7eb",
                                                        color: "#111827",
                                                        fontSize: "0.85rem",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            color: "#CC6828",
                                                            display: "inline-flex",
                                                            fontSize: "1.65rem",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getFileIcon(attachmentExtension)}
                                                    </span>
                                                    <span
                                                        style={{
                                                            minWidth: 0,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                display: "block",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {message.fileName || "Uploaded file"}
                                                        </span>
                                                        <span style={{ color: "#6b7280", textTransform: "uppercase" }}>
                                                            {attachmentExtension}
                                                        </span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenFile(attachmentUrl)}
                                                        aria-label="Open file"
                                                        style={{
                                                            border: "none",
                                                            background: "transparent",
                                                            padding: 0,
                                                            color: "#111827",
                                                            display: "inline-flex",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <FiExternalLink />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadFile(attachmentUrl, message.fileName || `attachment.${attachmentExtension}`)}
                                                        aria-label="Download file"
                                                        style={{
                                                            border: "none",
                                                            background: "transparent",
                                                            padding: 0,
                                                            color: "#111827",
                                                            display: "inline-flex",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <FiDownload />
                                                    </button>
                                                </div>
                                            )
                                        )}

                                        {(message.text || !attachmentUrl) && (
                                            <div
                                                style={{
                                                    width: "fit-content",
                                                    maxWidth: "24rem",
                                                    padding: "0.75rem 1rem",
                                                    borderRadius: "0.5rem",
                                                    backgroundColor: isUser ? "#CC6828" : "#e5e5ea",
                                                    color: isUser ? "#fff" : "#000",
                                                    whiteSpace: isUser ? "pre-wrap" : "normal",
                                                    lineHeight: 1.55,
                                                }}
                                            >
                                                {isUser ? message.text : renderChatText(message.text)}
                                            </div>
                                        )}
                                    </div>

                                    {/* RIGHT SIDE (for AI messages → icons after) */}
                                    {!isUser && (
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "6px",
                                                opacity: hoveredId === message.id ? 1 : 0,
                                                transition: "0.2s",
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleCopy(message.text, message.id)}
                                                onMouseEnter={() => setHoveredCopyId(message.id)}
                                                onMouseLeave={() => setHoveredCopyId(null)}
                                                aria-label={isCopied ? "Response copied" : "Copy response"}
                                                style={{
                                                    position: "relative",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "20px",
                                                    height: "20px",
                                                    padding: 0,
                                                    border: "none",
                                                    background: "transparent",
                                                    color: isCopied ? "#16a34a" : "#111827",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {isCopied ? <FiCheck /> : <FiCopy />}
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        bottom: "calc(100% + 8px)",
                                                        left: "50%",
                                                        transform: "translateX(-50%)",
                                                        padding: "6px 8px",
                                                        borderRadius: "6px",
                                                        backgroundColor: "#111827",
                                                        color: "#fff",
                                                        fontSize: "12px",
                                                        fontWeight: 500,
                                                        lineHeight: 1,
                                                        whiteSpace: "nowrap",
                                                        pointerEvents: "none",
                                                        opacity: hoveredCopyId === message.id || isCopied ? 1 : 0,
                                                        transition: "opacity 0.15s ease",
                                                        zIndex: 20,
                                                    }}
                                                >
                                                    {isCopied ? "Response copied" : "Copy response"}
                                                </span>
                                            </button>

                                            {speakingId === message.id ? (
                                                <FaRegStopCircle
                                                    onClick={handleStopSpeak}
                                                    style={{ cursor: "pointer", color: "red" }}
                                                />
                                            ) : (
                                                <FaVolumeUp
                                                    onClick={() => handleSpeak(message)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {isLoading && (
                        <p className="mt-2 ml-4 text-sm text-primary font-bold">
                            Generating response{" "}
                            <span className="loading-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </span>
                        </p>
                    )}

                    <div
                        style={{
                            borderTop: "1px solid #dee2e6",
                            backgroundColor: "#f8f9fa",
                            padding: "1rem",
                        }}
                    >
                        {file && (
                            <div
                                style={{
                                    display: "flex",
                                    marginBottom: "10px",
                                    justifyContent: 'center',
                                    maxWidth: "717px"
                                }}
                            >
                                <div
                                    style={{
                                        position: "relative",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        width: previewUrl ? "120px" : "220px",
                                    }}
                                >
                                    {previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            style={{
                                                width: "100%",
                                                height: "100px",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.75rem",
                                                padding: "0.85rem",
                                                background: "#fff",
                                                border: "1px solid #e5e7eb",
                                                textAlign: "left",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    color: "#CC6828",
                                                    fontSize: "1.8rem",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getFileIcon(getFileExtension(file?.name))}
                                            </span>
                                            <span style={{ minWidth: 0 }}>
                                                <span
                                                    style={{
                                                        display: "block",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        fontSize: "0.85rem",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {file?.name || "Selected file"}
                                                </span>
                                                <span
                                                    style={{
                                                        color: "#6b7280",
                                                        fontSize: "0.72rem",
                                                        textTransform: "uppercase",
                                                    }}
                                                >
                                                    {getFileExtension(file?.name)}
                                                </span>
                                            </span>
                                        </div>
                                    )}

                                    <div
                                        onClick={() => {
                                            setFile(null);
                                            setPreviewUrl(null);
                                            setUploadedFileMeta(null);
                                        }}
                                        style={{
                                            position: "absolute",
                                            top: "5px",
                                            right: "5px",
                                            background: "rgba(0,0,0,0.6)",
                                            color: "white",
                                            borderRadius: "50%",
                                            width: "22px",
                                            height: "22px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                        }}
                                    >
                                        ✕
                                    </div>
                                </div>
                            </div>
                        )}

                        {hasLowCredits && (
                            <div
                                style={{
                                    maxWidth: "520px",
                                    margin: "0 auto 1rem",
                                    padding: "1.75rem 1.75rem 1.5rem",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "14px",
                                    boxShadow: "0 1px 4px rgba(15, 23, 42, 0.08)",
                                    textAlign: "center",
                                }}
                            >
                                <p
                                    style={{
                                        margin: "0 0 1.25rem",
                                        color: "#8b93a3",
                                        fontSize: "1.05rem",
                                        lineHeight: 1.45,
                                        fontWeight: 600,
                                    }}
                                >
                                    Unfortunately, you have run out of AI credits. To continue using our AI tutor and career mentor, Clara, please top up your credits.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate.push("/aitutor/creditsbot")}
                                    style={{
                                        width: "100%",
                                        padding: "0.85rem 1rem",
                                        backgroundColor: "#ff7a14",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "10px",
                                        fontSize: "1rem",
                                        fontWeight: 700,
                                        cursor: "pointer",
                                    }}
                                >
                                    Top up now
                                </button>
                            </div>
                        )}

                        {!hasLowCredits && (
                            <div
                                style={{
                                    maxWidth: "768px",
                                    margin: "0 auto",
                                    display: "flex",
                                    gap: "0.5rem",
                                }}
                            >
                                <input
                                    type="text"
                                    placeholder="Ask anything"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSend()}
                                    style={{
                                        flex: 1,
                                        padding: "0.75rem",
                                        border: "1px solid #ccc",
                                        borderRadius: "0.375rem",
                                    }}
                                />

                                <label className="inline-flex items-center justify-center px-3 rounded-md bg-gray-200 hover:bg-gray-300 cursor-pointer">
                                    <FiUpload className="text-gray-700" />
                                    <input
                                        type="file"
                                        accept={SUPPORTED_FILE_ACCEPT}
                                        className="hidden"
                                        onChange={handleFileUpload}
                                    />
                                </label>

                                <button
                                    onClick={handleMicClick}
                                    className={`px-3 rounded-md ${isRecording ? "bg-red-500 animate-pulse" : "bg-gray-200 hover:bg-gray-300"
                                        }`}
                                >
                                    <FaMicrophone className={isRecording ? "text-white" : "text-gray-700"} />
                                </button>

                                {!isLoading ? (
                                    <button
                                        onClick={() => {
                                            if (input.trim() === "" && !uploadedFileMeta) return;

                                            handleSend();
                                        }}
                                        disabled={(input.trim() === "" && !uploadedFileMeta)}
                                        style={{
                                            padding: "0 1rem",
                                            backgroundColor: "#CC6828",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "0.375rem",
                                            cursor:
                                                (input.trim() === "" && !uploadedFileMeta)
                                                    ? "not-allowed"
                                                    : "pointer",
                                        }}
                                    >
                                        <IoIosSend />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStop}
                                        style={{
                                            padding: "0 1rem",
                                            backgroundColor: "#CC6828",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "0.375rem",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <FaRegStopCircle />
                                    </button>
                                )}
                            </div>
                        )}

                        {!hasLowCredits && (
                            <p
                                style={{
                                    fontSize: "0.75rem",
                                    color: "#6c757d",
                                    textAlign: "center",
                                    marginTop: "0.5rem",
                                }}
                            >
                                StudyCrowd.AI can make mistakes. Please check important info.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    updatedUserLoader: updatedUserLoader(state),
});
const mapDispatchToProps = (dispatch) => ({
    updateUser: (body) => dispatch(updateUser(body)),
});
